/*eslint-disable*/
import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Grid,
  Container,
  IconButton,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FONT_FAMILY } from "../../common/sharedFunctions.js";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { navigateLocalized } from "../../utils/i18nRoute";

export default function HomeFooter(props) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const settings = useSelector((state) => state.settingsdata.settings);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();

  // Colors
  const bgColor = isDarkMode ? "#0A0E14" : "#F8FAFC";
  const textPrimary = isDarkMode ? "#FFFFFF" : "#1E293B";
  const textSecondary = isDarkMode ? "#94A3B8" : "#475569";
  const accentColor = "#d4af37";
  const borderColor = isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
  const socialBg = isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)";

  const footerLinks = [
    { label: t("home", "Home"), path: "/" },
    { label: t("about", "About"), path: "/about-us" },
    { label: t("contact_us", "Contact"), path: "/contact-us" },
    { label: t("privacy_policy", "Privacy Policy"), path: "/privacy-policy" },
    { label: t("terms_conditions", "Terms & Conditions"), path: "/term-condition" },
    { label: t("cookie_policy", "Cookie Policy"), path: "/cookies" },
  ];

  return (
    <Box
      sx={{
        backgroundColor: bgColor,
        pt: { xs: 6, md: 8 },
        pb: 4,
        direction: isRTL === "rtl" ? "rtl" : "ltr",
      }}
    >
      {/* CTA Banner */}
      <Container maxWidth="lg" sx={{ mb: 6 }}>
        <Box
          sx={{
            backgroundColor: accentColor,
            borderRadius: "16px",
            p: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                color: "#000000",
                fontFamily: FONT_FAMILY,
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              {t("cta_title", "Need a chauffeur in Bordeaux?")}
            </Typography>
            <Typography
              sx={{
                color: "rgba(0,0,0,0.7)",
                fontFamily: FONT_FAMILY,
                fontSize: "0.9rem",
              }}
            >
              {t(
                "cta_subtitle",
                "Professional Chauffeurs & Absolute Discretion",
              )}
            </Typography>
          </Box>
          <Box
            onClick={() => navigateLocalized(navigate, "/booking", window.location.pathname)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              backgroundColor: "transparent",
              border: "2px solid rgba(0,0,0,0.3)",
              borderRadius: "9999px",
              px: 3,
              py: 1.5,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.1)",
                borderColor: "rgba(0,0,0,0.5)",
              },
            }}
          >
            <Typography
              sx={{
                color: "#000000",
                fontFamily: FONT_FAMILY,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {t("book_in_2min", "Book in 2 min")}
            </Typography>
            <ArrowForwardIcon sx={{ color: "#000000", fontSize: "1.25rem" }} />
          </Box>
        </Box>
      </Container>

      {/* Footer Links */}
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Logo & Description */}
          <Grid item xs={12} md={4}>
            <Typography
              onClick={() => navigateLocalized(navigate, "/", window.location.pathname)}
              sx={{
                color: accentColor,
                fontFamily: FONT_FAMILY,
                fontSize: "1.5rem",
                fontWeight: 700,
                mb: 2,
                letterSpacing: "0.5px",
                cursor: "pointer"
              }}
            >
              {t("driver_footer")}<span style={{ color: textPrimary }}>{t("2go_footer")}</span>
            </Typography>
            <Typography
              sx={{
                color: textSecondary,
                fontFamily: FONT_FAMILY,
                fontSize: "0.875rem",
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              {t(
                "footer_description",
                "Your premium private chauffeur service in Bordeaux and its surroundings. Professionalism, comfort, and safety.",
              )}
            </Typography>
          </Grid>

          {/* Quicklinks */}
          <Grid item xs={12} sm={6} md={5}>
            <Typography
              sx={{
                color: textPrimary,
                fontFamily: FONT_FAMILY,
                fontSize: "1.1rem",
                fontWeight: 700,
                mb: 2,
              }}
            >
              {t("quick_links", "Quicklinks")}
            </Typography>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1
            }}>
              {footerLinks.map((link, index) => (
                <Typography
                  key={index}
                  onClick={() => navigateLocalized(navigate, link.path, window.location.pathname)}
                  className="footerLink"
                  sx={{
                    color: textSecondary,
                    fontFamily: FONT_FAMILY,
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    transition: "color 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": { color: accentColor },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Follow Us */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              sx={{
                color: textPrimary,
                fontFamily: FONT_FAMILY,
                fontSize: "1.1rem",
                fontWeight: 700,
                mb: 2,
              }}
            >
              {t("follow_us", "Follow Us")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <IconButton
                onClick={() => settings?.FacebookHandle && window.open(settings.FacebookHandle, "_blank")}
                disabled={!settings?.FacebookHandle}
                sx={{
                  backgroundColor: socialBg,
                  color: textSecondary,
                  "&:hover": {
                    backgroundColor: accentColor,
                    color: "#000000",
                  },
                }}
              >
                <FacebookIcon fontSize="medium" />
              </IconButton>
              <IconButton
                onClick={() => settings?.TwitterHandle && window.open(settings.TwitterHandle, "_blank")}
                disabled={!settings?.TwitterHandle}
                sx={{
                  backgroundColor: socialBg,
                  color: textSecondary,
                  "&:hover": {
                    backgroundColor: accentColor,
                    color: "#000000",
                  },
                }}
              >
                <TwitterIcon fontSize="medium" />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        {/* Copyright */}
        <Box
          sx={{
            borderTop: `1px solid ${borderColor}`,
            mt: 6,
            pt: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: textSecondary,
              fontFamily: FONT_FAMILY,
              fontSize: "0.8rem",
            }}
          >
            © {new Date().getFullYear()}{" "}
            {settings?.CompanyName || "DRIVER 2GO"}.{" "}
            {t("all_rights_reserved", "All rights reserved.")}
          </Typography>
          {settings?.CompanyWebsite && (
            <Typography
              component="a"
              className="footerLink"
              href={settings.CompanyWebsite}
              target="_blank"
              sx={{
                color: textSecondary,
                fontFamily: FONT_FAMILY,
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { color: accentColor },
              }}
            >
              {settings.CompanyWebsite}
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
}

HomeFooter.propTypes = {
  whiteFont: PropTypes.bool,
};
